import Big from "big.js";

import {
  DecisionCriteriaMismatchNeededLoanAmountLowExplainer,
  DecisionCriteriaMismatchNeededLoanAmountHighExplainer,
  DecisionCriteriaMismatchMaxLoanAmountLowExplainer,
  DecisionCriteriaMismatchPropertyTypeExplainer,
  DecisionCriteriaMismatchPropertyUsageExplainer,
  DecisionCriteriaMismatchCreditScoreExplainer,
  DecisionCriteriaMismatchReasons as Reasons,
  DecisionCriteriaMismatchCodes as Codes,
  DecisionCriteriaMismatch as Mismatch,
  PropertyTypes,
  PropertyUsages,
  DecisionCriteriaMismatchExpectationLabels,
} from "@eave/shared/types/api";

import { ceilBig, floorBig } from "./big-helpers";
import { formatCurrency } from "./currency";

// this reduces the decision criteria mismatch explainers each to 1 path to resolve them, based on business rules
// so that the path forward resolution can be shared without the exact text of the resolution message
// needing to be the same across uses

// eg for example, for the needed loan amount too high issue,
// that could be resolved by either:
// a) increasing the downpayment
// b) decreasing the purchase price
// c) potentially qualifying for a higher loan amount by changing factors on the application
// the business choice is made here that a) is the simplest way to resolve

export const mismatchNeededLoanAmountToAllowedBounds = (
  explainer: (
    | DecisionCriteriaMismatchNeededLoanAmountLowExplainer
    | DecisionCriteriaMismatchNeededLoanAmountHighExplainer
  )[],
  purchasePrice: number,
) => {
  const [{ reason, expectation }] = explainer;

  switch (reason) {
    case Reasons.NeededLoanAmountTooHigh: {
      const maxLoanAmount = expectation;
      const minDownpayment = ceilBig(Big(purchasePrice).sub(maxLoanAmount));
      return {
        MinDownpayment: minDownpayment.toFixed(),
      };
    }
    case Reasons.NeededLoanAmountTooLow: {
      const minLoanAmount = expectation;
      const maxDownpayment = floorBig(Big(purchasePrice).sub(minLoanAmount));
      return {
        MaxDownpayment: maxDownpayment.toFixed(),
      };
    }
    default:
      throw new Error(`'${reason}' is not an expected reason`);
  }
};

export const mismatchMaxLoanAmountToAllowedBounds = (
  explainer: DecisionCriteriaMismatchMaxLoanAmountLowExplainer[],
  purchasePrice: number,
) => {
  // only reason is: max loan amount for this situation, based on closest fit decision criteria,
  // is less than that decision criteria's min loan amount.
  // that's because: the max LTV for the situation at criteria x the purchase price is too low.
  // since max LTV does not vary with purchase price, can see rates
  // by increasing purchase price and adjusting downpayment to fit min loan for criteria.
  const [{ expectation, actual }] = explainer;
  const minLoanAmountAtCriteria = ceilBig(Big(expectation));

  // since cannot represent all decimals precisely, and want to be sure the purchase price yielded
  // is >= actual min purchase price (otherwise, customer will see same error msg repeatedly).
  const maxLTV = Big(actual).div(purchasePrice).round(2, 0);

  const neededPurchasePrice = ceilBig(minLoanAmountAtCriteria.div(maxLTV));
  const neededDownpayment = neededPurchasePrice.sub(minLoanAmountAtCriteria);

  return {
    MinPurchasePrice: neededPurchasePrice.toFixed(),
    Downpayment: neededDownpayment.toFixed(),
  };
};

export const mismatchNeededLoanAmountToErrorMsg = (
  explainer: (
    | DecisionCriteriaMismatchNeededLoanAmountLowExplainer
    | DecisionCriteriaMismatchNeededLoanAmountHighExplainer
  )[],
  purchasePrice: number,
) => {
  const bounds = mismatchNeededLoanAmountToAllowedBounds(explainer, purchasePrice);

  if (bounds.MinDownpayment) {
    return `Minimum downpayment is ${formatCurrency(bounds.MinDownpayment)}`;
  }

  if (bounds.MaxDownpayment) {
    return `Maximum downpayment is ${formatCurrency(bounds.MaxDownpayment)}`;
  }

  return "";
};

export const mismatchMaxLoanAmountToErrorMsg = (
  explainer: DecisionCriteriaMismatchMaxLoanAmountLowExplainer[],
  purchasePrice: number,
) => {
  const bounds = mismatchMaxLoanAmountToAllowedBounds(explainer, purchasePrice);

  return `Minimum purchase price is ${formatCurrency(
    bounds.MinPurchasePrice,
  )}, with downpayment of ${formatCurrency(bounds.Downpayment)}`;
};

export const mismatchBorrowerToErrorMsg = (
  explainer: DecisionCriteriaMismatchCreditScoreExplainer[],
) => {
  return `Minimum credit score is ${explainer[0].expectation}`;
};

const mismatchPropertyTypeErrorMessage = (
  explainer: DecisionCriteriaMismatchPropertyTypeExplainer,
  propertyTypeToDisplay: { [key in PropertyTypes]: string | string[] },
): string => {
  const propertyTypes = new Set<string>();

  explainer.expectation.forEach(propertyType => {
    const display = propertyTypeToDisplay[propertyType];

    if (typeof display === "string") {
      propertyTypes.add(display);
    } else if (Array.isArray(display)) {
      display.forEach(type => {
        propertyTypes.add(type);
      });
    }
  });

  return `Allowed property types are ${Array.from(propertyTypes).filter(Boolean).join(", ")}`;
};

const mismatchPropertyUsageErrorMessage = (
  explainer: DecisionCriteriaMismatchPropertyUsageExplainer,
  propertyUsageToDisplay: { [key in PropertyUsages]: string | string[] },
): string => {
  const propertyUsages = new Set<string>();

  explainer.expectation.forEach(propertyUsage => {
    const display = propertyUsageToDisplay[propertyUsage];

    if (typeof display === "string") {
      propertyUsages.add(display);
    } else if (Array.isArray(display)) {
      display.forEach(usage => {
        propertyUsages.add(usage);
      });
    }
  });

  return `Allowed property usages are ${Array.from(propertyUsages).filter(Boolean).join(", ")}`;
};

export const mismatchPropertyToErrorMsg = (
  explainer: (
    | DecisionCriteriaMismatchPropertyTypeExplainer
    | DecisionCriteriaMismatchPropertyUsageExplainer
  )[],
  propertyTypeToDisplay: { [key in PropertyTypes]: string | string[] },
  propertyUsageToDisplay: { [key in PropertyUsages]: string | string[] },
) => {
  const reasons: string[] = explainer.map(e => {
    if (
      e.reason === Reasons.PropertyType &&
      e.expectation_label === DecisionCriteriaMismatchExpectationLabels.AllowedValues
    ) {
      return mismatchPropertyTypeErrorMessage(e, propertyTypeToDisplay);
    }

    if (
      e.reason === Reasons.PropertyUsage &&
      e.expectation_label === DecisionCriteriaMismatchExpectationLabels.AllowedValues
    ) {
      return mismatchPropertyUsageErrorMessage(e, propertyUsageToDisplay);
    }

    return "";
  });

  return reasons.filter(Boolean).join("\n");
};

export const decisionCriteriaMismatchToErrorMsg = (
  mismatch: Mismatch,
  input: { purchasePrice: string },
  propertyTypeToDisplay: { [key in PropertyTypes]: string | string[] },
  propertyUsageToDisplay: { [key in PropertyUsages]: string | string[] },
): string => {
  const defaultMessage = "Sorry, we don't have a loan program for these parameters";

  switch (mismatch.code) {
    case Codes.NeededLoanAmount:
      return mismatchNeededLoanAmountToErrorMsg(mismatch.explainer, Number(input.purchasePrice));
    case Codes.MaxLoanAmount:
      return mismatchMaxLoanAmountToErrorMsg(mismatch.explainer, Number(input.purchasePrice));
    case Codes.Borrower:
      return mismatchBorrowerToErrorMsg(mismatch.explainer);
    case Codes.Property: {
      const message = mismatchPropertyToErrorMsg(
        mismatch.explainer,
        propertyTypeToDisplay,
        propertyUsageToDisplay,
      );
      if (message === "") {
        return defaultMessage;
      }

      return message;
    }
    default: {
      return defaultMessage;
    }
  }
};
